import React from "react";
import { computePercentDifference } from "./pricingStrategyFormatting";

// The prominent generated-result preview shown inside a StrategyCard once a
// Strategy has been generated. Two variants (pricing vs. listing
// description) because the two result shapes have nothing in common, but
// both share the same typographic scale so a Pricing card and a Listing
// Description card next to each other read as one consistent system.

const PRICE_SIZE = "24px";

/**
 * @param {string} label - "Recommended initial offer" (Buyer) or "Recommended list price" (Seller)
 * @param {string} recommendedPrice - already-formatted, e.g. "$1,095,000"
 * @param {string} [comparePrice] - current asking/list price, for the "X% below/above asking" line
 * @param {string} [estimatedValueLow]
 * @param {string} [estimatedValueHigh]
 */
export function PricingResultPreview({ label, recommendedPrice, comparePrice, estimatedValueLow, estimatedValueHigh }) {
  if (!recommendedPrice) return null;
  const diff = comparePrice ? computePercentDifference(comparePrice, recommendedPrice) : null;
  return (
    <div>
      {label && (
        <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.02em" }}>
          {label}
        </div>
      )}
      <div style={{ fontSize: PRICE_SIZE, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "#1b1c1d", marginTop: "2px" }}>
        {recommendedPrice}
      </div>
      {diff && Math.abs(diff.percentDiff) >= 0.1 && (
        <div style={{ fontSize: "0.85em", fontWeight: 600, color: diff.percentDiff <= 0 ? "#1e8e3e" : "#c0392b", marginTop: "2px" }}>
          {Math.abs(diff.percentDiff).toFixed(1)}% {diff.percentDiff <= 0 ? "below" : "above"} asking
        </div>
      )}
      {estimatedValueLow && estimatedValueHigh && (
        <div className="text-grey" style={{ fontSize: "0.78em", marginTop: "6px" }}>
          Estimated market value<br />
          <span style={{ fontWeight: 600 }}>{estimatedValueLow} – {estimatedValueHigh}</span>
        </div>
      )}
    </div>
  );
}

const WRITING_STYLE_LABEL = {
  professional: "Professional",
  warm: "Warm & Inviting",
  luxury: "Luxury",
  concise: "Concise",
};

/**
 * @param {string} [writingStyle]
 * @param {string} [previewText] - a short snippet (e.g. the MLS description), already truncated by the caller
 */
export function ListingDescriptionResultPreview({ writingStyle, previewText }) {
  return (
    <div>
      {writingStyle && (
        <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, marginBottom: "4px" }}>
          {WRITING_STYLE_LABEL[writingStyle] || writingStyle} style
        </div>
      )}
      {previewText && (
        <div style={{ fontSize: "0.85em", color: "#444444", lineHeight: 1.4 }}>
          {previewText}
        </div>
      )}
    </div>
  );
}

export function truncate(text, maxLength = 110) {
  if (!text) return null;
  const trimmed = text.trim();
  return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength).trim()}…` : trimmed;
}
